import type { AtLeast, Overwrite } from '@vartion/ui'
import { acceptHMRUpdate, defineStore } from 'pinia'

import { type CaseFilterFilters } from '~/composables/useCases.ts'
import { bus } from '~/services/bus.ts'
import { type Batch, type CaseModel, type CaseModelDetailed } from '~/types.ts'

export interface CaseAssignRequest {
  user_id?: null | number
  group_id?: null | number
  filters: AtLeast<CaseFilterFilters, 'id'>
}

export type UpdateCaseRequest = Partial<
  Overwrite<
    CaseModel,
    { clients: number[]; passportVerificationForm?: Record<string, any>; search?: boolean; reset_searched_at?: boolean; on_hold_audit_description?: string; labels: string[] }
  >
>

export const useCases = defineStore('cases', {
  actions: {
    async load(idOrUUID: string | number, relations: string[] = []) {
      await stores.loading.update({ cases: true })

      let caseModel: CaseModelDetailed | null = null

      try {
        const { data } = await api.get<CaseModelDetailed>(`cases/${idOrUUID}`, { params: { with: relations }, key: null })
        caseModel = data
      } catch {
        $message.error($t('api-error'))
      }

      await stores.loading.update({ cases: false })

      return caseModel
    },

    async update(id: number, payload: UpdateCaseRequest) {
      try {
        await stores.loading.update({ cases: true })

        const { data } = await api.patch<Omit<CaseModel<['clients']>, 'organization'>>(`v1/cases/${id}`, payload)

        if (data.clients.length) {
          // The response doesn't contain the 'type' of clients, so we need to fetch it with the 'type' included.
          const { data: caseModel } = await api.get<CaseModel<['clients']>>(`cases/${id}`, { params: { with: ['clients'] }, key: null })
          data.clients = caseModel.clients
        }

        await stores.loading.update({ cases: false })

        return data
      } catch (error) {
        console.error(error)
        $message.error($t('api-error'))
        await stores.loading.update({ cases: false })
      }
    },

    async remove(ids: number[]) {
      try {
        const { data } = await api.delete<Batch>('cases', { data: { filters: { id: ids } } })

        bus.emit('batch.created', data)
        bus.until('batch.finished', (id) => {
          if (id === data.id) {
            bus.emit('cases.fetch')

            return true
          }

          return false
        })

        for (const id of ids) {
          if (stores.case.case?.id === id) {
            stores.case.reset()
          }
        }

        return data
      } catch (error) {
        console.error(error)
        $message.error($t('api-error'))
      }
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCases, import.meta.hot))
}
